
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { namespace } from 'vuex-class';
import { vuex } from "@/store";
import _ from 'lodash';
import rest from '@/rest';
import { List } from 'linq-collections';
import MediaPool from './MediaPool.vue';
import { FileDetailsVm } from '@/interfaces/FileDetailsVm';
import AudioPlayerWithSubtitles from '../Shared/AudioPlayerWithSubtitles.vue';
import AiChatModule from './AiChatModule.vue';
import { AiPodcastCreatorVm, PodcastTextVm } from '@/interfaces/AiPodcastCreatorVm';
import { CourseElementVm } from '@/interfaces/CourseElementVm';
import { ParamDictionary } from '@/interfaces/ParamDictionary';
import { signalrEventBus } from '@/main';
import { CustomizationVm } from '@/interfaces/CustomizationVm';
import TtsVoiceSelection from './TtsVoiceSelection.vue';
import L3FrameWithTitle from '../Shared/L3FrameWithTitle.vue';
import SimpleMessageBox from '../Shared/SimpleMessageBox.vue';

const globals = namespace("globals");
const ux = namespace("ux");
const customization = namespace("customization");

@Component({
  components: {
    L3FrameWithTitle,
    MediaPool,
    AudioPlayerWithSubtitles,
    AiChatModule,
    TtsVoiceSelection,
    SimpleMessageBox
  }
})
export default class AiPodcastCreatorDlg extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;
  @PropSync("podcastElement") syncedPodcastElement!: CourseElementVm;
  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedBundle: any;
  @globals.Getter selectedExercise: any;
  @globals.Action refreshSelectedElement!: () => Promise<void>;
  @ux.Getter gradientBackground: any;
  @ux.Getter gradientBackground2: any;
  @ux.Getter gradientBackground3: any;
  @ux.Getter gradientText: any;
  @ux.State darkTheme: any;

  isBusyOnDescriptionSuggestion = false;
  isBusyOnTargetGroupAndToneSuggestion = false;
  isBusySavingData = false;
  isBusyCreatingDialog = false;
  isBusyCreatingAudio = false;
  isValid = false;
  showConfirmationDialog = false;
  showImportDialog = false;
  importText = '';
  clipboardCopied = false;
  fileWithSummarySelected = false;
  formData: AiPodcastCreatorVm | null = null;
  originalFormData: AiPodcastCreatorVm | null = null;
  fileList: FileDetailsVm[] | null = null;
  // chatMessages: ChatMessageVm[] = [];
  id = "";
  audioCreationProgress = 0;

  $refs!: {
    mediaPool: MediaPool;
  };

  created() {
    // SignalR event listener
    signalrEventBus.$on('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  beforeDestroy() {
    // clearAllBodyScrollLocks();
    // Make sure to cleanup SignalR event handlers when removing the component
    signalrEventBus.$off('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  // SignalR events
  async backgroundTaskEvent(response: { id: string, progress: number, message: string, status: string }) {
    if (this.id !== response.id) return;
    console.log("AiPodcastCreatorDlg > Background task event:", response);

    if (response.status == "INF") {
      this.audioCreationProgress = response.progress;
      return;
    }

    await this.refreshSelectedElement();
    this.audioCreationProgress = 0;
    this.isBusyCreatingAudio = false;

    if (response.status == "SUC") {
      this.syncedShowDialog = false;
    }
  }

  @Watch("syncedShowDialog")
  async onShowDialogChanged(val: boolean) {
    if (this.syncedShowDialog) {
      // On dialog open
      this.isBusySavingData = false;
      this.isBusyCreatingDialog = false;
      this.isBusyCreatingAudio = false;
      this.audioCreationProgress = 0;

      await this.loadAiPodcastData();
      // console.log("AiPodcastCreatorDlg: Language ", this.formData?.language);
      this.onFileSelectionChanged();
      if (this.formData && !this.formData.language) {
        // Default language code (Deutsch)
        console.log("AiPodcastCreatorDlg: Setting default language code to de-DE");
        this.formData!.language = "de-DE";
      }
      // if (this.formData && !this.formData.hostLanguage) {
      //   this.formData!.hostLanguage = "Deutsch";
      // }
      // if (this.formData && !this.formData.hostVoice) {
      //   // this.formData!.hostVoice = "de-DE-Wavenet-F";
      //   this.formData!.hostVoice = "female";
      // }
      // if (this.formData && !this.formData.guestLanguage) {
      //   this.formData!.guestLanguage = "Deutsch";
      // }
      // if (this.formData && !this.formData.guestVoice) {
      //   // this.formData!.guestVoice = "de-DE-Standard-B";
      //   this.formData!.guestVoice = "male";
      // }

      // Disable body scroll on iOS
      this.$nextTick(async () => {
        await this.$globalHelper.delay(10);
        const modal = document.querySelector('.modal');
        disableBodyScroll(modal);
      });
    } else {
      // Dialog closing
      clearAllBodyScrollLocks();
    }
  }

  get languageOptions() {
    return this.$globalHelper.euLanguageOptions;
  }

  async loadAiPodcastData() {
    this.isBusySavingData = true;
    let params = <ParamDictionary>{dictionary: {}};
    params.dictionary!["BundleId"] = this.selectedBundle.id.toString();
    params.dictionary!["ExerciseId"] = this.selectedExercise.id.toString();
    params.dictionary!["FileName"] = this.syncedPodcastElement.file!;
    params.dictionary!["ElementGuid"] = this.syncedPodcastElement.guid!;

    this.formData = null;
    await rest.url("contentManager/loadAiPodcastCreatorData")
      .post(params)
      .then((response) => {
        // console.log("AiPodcastCreatorDlg - loadAiPodcastData language:", response?.language);
        this.formData = response;
        this.formData!.bundleId = this.selectedBundle.id;

        if (this.formData && !this.formData.dialog) {
          this.formData.dialog = [];
        }

        if (this.formData!.dialog!.length == 0)
          this.formData!.dialog!.push(<PodcastTextVm>{ id: 1, speaker: 'Host', text: 'Hallo'});

        // Keep a deep copy of the loaded data to detect unsaved changes
        this.originalFormData = _.cloneDeep(this.formData);
        console.log("AiPodcastCreatorDlg - loadAiPodcastData:", this.formData);
        console.log("AiPodcastCreatorDlg - originalFormData:", this.originalFormData);
      })
      .finally(() => {
        this.isBusySavingData = false;
      });
  }

  async saveAiPodcastData() {
    this.isBusySavingData = true;
    if (this.customizationSettings.enableAi) {
      this.fileList = this.$refs.mediaPool.getSelectedFiles();
      this.formData!.mediaAiSummaryFiles = this.fileList;
    }

    this.syncedPodcastElement.title = this.formData?.title || '';
    this.syncedPodcastElement.text = this.formData?.text || '';

    await rest.url("contentManager/saveAiPodcastCreatorData")
      .post(this.formData)
      .then((response) => {
        // After a successful save, update the original snapshot so further closes don't prompt
        this.originalFormData = _.cloneDeep(this.formData);
      })
      .finally(() => {
        this.isBusySavingData = false;
      });
  }

  onDeleteMessage(message: PodcastTextVm) {
    if (!this.formData || !this.formData.dialog) return;
    let index = this.formData.dialog.indexOf(message);
    if (index == -1) return;
    this.formData.dialog.splice(index, 1);
    if (this.formData!.dialog!.length == 0)
      this.formData!.dialog!.push(<PodcastTextVm>{ id: 1, speaker: 'Host', text: 'Hallo'});
    this.updateDialogIds();
  }

  onAddMessage(message: PodcastTextVm) {
    if (!this.formData || !this.formData.dialog) return;
    let index = this.formData.dialog.indexOf(message);
    if (index == -1) return;
    let newMessage = <PodcastTextVm>{ id: 0, speaker: (message.speaker == 'Host' ? 'Guest' : 'Host'), text: '' }
    this.formData.dialog.splice(index + 1, 0, newMessage);
    this.updateDialogIds();
  }

  updateDialogIds() {
    if (!this.formData || !this.formData.dialog) return;
    for (let i = 0; i < this.formData.dialog.length; i++) {
      this.formData.dialog[i].id = i + 1;
    }
  }

  async onPodcastDescriptionSuggestion() {
    this.isBusyOnDescriptionSuggestion = true;
    this.fileList = this.$refs.mediaPool.getSelectedFiles();
    this.formData!.mediaAiSummaryFiles = this.fileList;

    await rest.url("contentManager/suggestAiPodcastContentDescription")
      .post(this.formData)
      .then((response) => {
        if (! this.formData) return;
        this.formData.text = response.text;
      })
      .finally(() => {
        this.isBusyOnDescriptionSuggestion = false;
      });
  }

  async onTargetGroupAndToneSuggestion() {
    this.isBusyOnTargetGroupAndToneSuggestion = true;
    this.fileList = this.$refs.mediaPool.getSelectedFiles();
    this.formData!.mediaAiSummaryFiles = this.fileList;

    await rest.url("contentManager/suggestAiPodcastTargetGroupAndTone")
      .post(this.formData)
      .then((response) => {
        if (! this.formData) return;
        this.formData.targetGroup = response.targetGroup;
        this.formData.tone = response.tone;
      })
      .finally(() => {
        this.isBusyOnTargetGroupAndToneSuggestion = false;
      });
  }

  async onCreatePodcastDialog() {
    this.isBusyCreatingDialog = true;
    this.fileList = this.$refs.mediaPool.getSelectedFiles();
    this.formData!.mediaAiSummaryFiles = this.fileList;

    await rest.url("contentManager/createPodcastDialog")
      .post(this.formData)
      .then((response) => {
        if (!this.formData) return;
        this.formData.dialog = response.dialog;
        this.syncedPodcastElement.createdWithAi = true;

        // this.$root.$emit('moduleUpdated:CourseCreator', this.formData);
        // this.syncedShowDialog = false;
      })
      .finally(() => {
        this.isBusyCreatingDialog = false;
      });
  }

  async onCreateAudio() {
    if (!this.formData || this.isBusyCreatingAudio) return;
    this.isBusyCreatingAudio = true;

    await this.$l3rnOnlineHub.ensureConnection();
    this.formData.signalRClientId =  this.$l3rnOnlineHub.ConnectionId!;

    await this.saveAiPodcastData();
    // this.$emit('complete', this.formData);
    rest.url("contentManager/CreatePodcastAudio")
      .post(this.formData)
      .then(async (response) => {
        this.syncedPodcastElement.contentUrl = response;
        this.syncedPodcastElement.createdWithAi = true;
        this.id = this.$globalHelper.extractFileName(this.syncedPodcastElement.contentUrl ?? "");
        // console.log("Podcast ID: ", this.id);

        await this.saveAiPodcastData();
        // this.syncedShowDialog = false;
      })
      .finally(() => {
      });
  }

  async onClose() {
    // If we don't have loaded data, just close
    if (!this.formData || !this.originalFormData) {
      this.$emit('closed:AiPodcastCreator');
      this.syncedShowDialog = false;
      return;
    }

    // If nothing changed, close immediately
    if (_.isEqual(this.formData, this.originalFormData)) {
      this.$emit('closed:AiPodcastCreator');
      this.syncedShowDialog = false;
      return;
    }

    // Otherwise ask for confirmation
    this.showConfirmationDialog = true;
  }

  async onCloseEditor(save: boolean) {
    this.showConfirmationDialog = false;
    if (save) {
      await this.saveAiPodcastData();
    }
    this.$emit('closed:AiPodcastCreator');
    this.syncedShowDialog = false;
  }

  onFileSelectionChanged() {
    if (!this.$refs.mediaPool) return;
    let files = this.$refs.mediaPool.getSelectedFiles().filter(f => f.hasAiSummary);
    this.fileWithSummarySelected = files.length > 0;
  }

  get enableTargetGroupAndToneSuggestion() {
    if (!this.formData) return false;
    if (this.formData.text?.length ?? 0 > 0) return true;
    if (this.fileWithSummarySelected) return true;

    return false;
  }

  getChatBubbleColor(speaker: string) {
    if (speaker == 'Host') {
      return '#e0e0e0'; // grey lighten-2
    }
    if (this.darkTheme) {
      return this.$store.state.ux.colorAccent + '60';
    } else {
      return this.$store.state.ux.colorAccent + 'e0';
    }
  }

  onOpenEditDialog() {
    this.importText = this.formData?.dialog?.map(m => `[${m.speaker}]\n${m.text}`).join('\n') ?? '';
    this.showImportDialog = true;
  }

  async copyDialogToClipboard() {
    if (!this.formData?.dialog) return;
    this.clipboardCopied = true;
    const text = this.formData.dialog
      .map(m => `[${m.speaker}]\n${m.text}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    await this.$globalHelper.delay(2000);
    this.clipboardCopied = false;
  }

  onImportDialog() {
    if (!this.formData || !this.importText.trim()) return;
    const lines = this.importText.split('\n');
    const parsed: PodcastTextVm[] = [];
    let current: PodcastTextVm | null = null;

    for (const line of lines) {
      const match = line.match(/^\[(Host|Guest)\]\s*(.*)$/);
      if (match) {
        if (current) {
          if (current.text) {
            current.text = current.text.trim();
          }
          parsed.push(current);
        }
        current = <PodcastTextVm>{ id: 0, speaker: match[1], text: match[2] };
      } else if (current && line.trim().length > 0) {
        // continuation line (skip blank lines)
        current.text += ((current.text ?? '').length > 0 ? '\n' : '') + line;
      }
    }
    if (current) {
      const last = current;
      if (last.text) {
        last.text = last.text.trim();
      }
      parsed.push(last);
    }

    if (parsed.length === 0) return;
    this.formData.dialog = parsed;
    this.updateDialogIds();
    this.showImportDialog = false;
    this.importText = '';
  }

  get dialogBackground() {
    if (this.darkTheme)
      return "#616161";
    else
      return "#d0d0d0";
  }
}
